import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Check,
  Menu,
  X,
  Globe,
  Code,
  Palette,
  Megaphone,
  Server,
  Briefcase,
  GraduationCap,
  Camera,
  PenTool,
  Video,
  Music,
  Dumbbell,
  Stethoscope,
  Scale,
  Building2,
  Users,
  Sparkles,
  ShoppingBag,
  Wrench,
  BookOpen,
  HeartHandshake
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MorphingNavbar from '../../components/MorphingNavbar';

// ============================================
// USE CASE DATA
// ============================================

const USE_CASE_CATEGORIES = [
  {
    id: 'tech-digital',
    title: 'Tech & Digital Services',
    description: 'Perfect for technology and digital service providers',
    useCases: [
      {
        icon: Server,
        title: 'WordPress Hosting Agencies',
        slug: 'wordpress-hosting',
        description: 'Manage hosting plans, maintenance packages, and support subscriptions for WordPress clients.',
        challenges: [
          'Tracking dozens of hosting accounts and renewal dates',
          'Manually sending renewal reminders',
          'Handling support requests via email chaos',
          'No visibility into monthly recurring revenue'
        ],
        solutions: [
          'Automated expiration tracking and email reminders',
          'Client self-service portal for renewals',
          'Built-in support ticket system',
          'Real-time MRR dashboard and analytics'
        ],
        testimonial: {
          quote: 'We went from spreadsheets to a real client portal in one afternoon. Our clients love the self-service options.',
          author: 'Maria K.',
          role: 'Founder, WP Care Agency'
        }
      },
      {
        icon: Code,
        title: 'Web Development Studios',
        slug: 'web-development',
        description: 'Sell maintenance retainers, hosting bundles, and ongoing development hours.',
        challenges: [
          'Clients forget what\'s included in their retainer',
          'Tracking billable hours across projects',
          'Chasing payments for completed work',
          'Looking unprofessional with manual invoicing'
        ],
        solutions: [
          'Clear service packages with feature lists',
          'Stripe-powered automatic billing',
          'Professional branded client portal',
          'Knowledge base for client self-service'
        ],
        testimonial: {
          quote: 'Our retainer renewals went from 60% to 90% since launching our Edrano portal.',
          author: 'James T.',
          role: 'Lead Developer, Code & Canvas'
        }
      },
      {
        icon: Globe,
        title: 'SEO & Digital Marketing Agencies',
        slug: 'seo-agencies',
        description: 'Package SEO audits, monthly optimization, and reporting into recurring revenue.',
        challenges: [
          'Explaining what clients get each month',
          'Delivering reports via email attachments',
          'Upselling additional services',
          'Managing multiple client campaigns'
        ],
        solutions: [
          'Tiered service packages with clear deliverables',
          'Knowledge base for client education',
          'AI chatbot for instant service recommendations',
          'Centralized client and service management'
        ],
        testimonial: {
          quote: 'The AI chatbot actually converts website visitors into qualified leads while I sleep.',
          author: 'Sarah M.',
          role: 'CEO, Rank & Rise Digital'
        }
      },
      {
        icon: Wrench,
        title: 'IT Support & MSPs',
        slug: 'it-support',
        description: 'Deliver managed services, help desk support, and technology consulting.',
        challenges: [
          'Clients calling for simple issues',
          'Tracking service agreements and SLAs',
          'Managing team member assignments',
          'Billing for out-of-scope work'
        ],
        solutions: [
          'Self-service knowledge base reduces calls by 60%',
          'Service tracking with expiration alerts',
          'Team management with role-based access',
          'Clear service tiers with scope definitions'
        ],
        testimonial: {
          quote: 'Support tickets dropped 50% after we launched the knowledge base. Clients find answers themselves now.',
          author: 'Mike R.',
          role: 'Founder, TechFirst MSP'
        }
      }
    ]
  },
  {
    id: 'creative',
    title: 'Creative & Design Services',
    description: 'For creative professionals selling ongoing services',
    useCases: [
      {
        icon: Palette,
        title: 'Graphic Design Studios',
        slug: 'graphic-design',
        description: 'Sell design retainers, brand packages, and monthly creative support.',
        challenges: [
          'Scope creep on unlimited revisions',
          'Clients requesting work outside their package',
          'No clear pricing for ad-hoc requests',
          'Losing track of project hours'
        ],
        solutions: [
          'Defined service packages with clear scope',
          'Easy upsell flow for additional services',
          'Client portal shows exactly what\'s included',
          'Professional checkout for one-off projects'
        ],
        testimonial: {
          quote: 'Finally, my clients understand what they\'re paying for. No more awkward scope discussions.',
          author: 'Lisa P.',
          role: 'Creative Director, PixelPerfect Studio'
        }
      },
      {
        icon: Video,
        title: 'Video Production Companies',
        slug: 'video-production',
        description: 'Package video editing, monthly content, and production retainers.',
        challenges: [
          'Complex pricing for different video types',
          'Managing revision rounds',
          'Collecting payments before delivery',
          'Client communication scattered across channels'
        ],
        solutions: [
          'Tiered packages for different video needs',
          'Stripe payments before project starts',
          'Centralized client communication',
          'Service catalog with clear deliverables'
        ],
        testimonial: {
          quote: 'We package our video services like products now. Revenue is more predictable than ever.',
          author: 'David K.',
          role: 'Founder, MotionWorks Media'
        }
      },
      {
        icon: Camera,
        title: 'Photography Studios',
        slug: 'photography',
        description: 'Sell photo packages, editing subscriptions, and event coverage.',
        challenges: [
          'Taking deposits and final payments',
          'Managing booking calendar',
          'Delivering photos to clients',
          'Upselling prints and albums'
        ],
        solutions: [
          'Package-based pricing with deposits',
          'Calendar integration for bookings',
          'Professional client portal experience',
          'Easy add-on services at checkout'
        ],
        testimonial: {
          quote: 'My booking process is so professional now. Clients pay deposits instantly.',
          author: 'Emma S.',
          role: 'Owner, Captured Moments Photography'
        }
      },
      {
        icon: PenTool,
        title: 'Copywriting & Content Agencies',
        slug: 'copywriting',
        description: 'Deliver content packages, blog subscriptions, and writing retainers.',
        challenges: [
          'Tracking word counts and deliverables',
          'Managing multiple client content calendars',
          'Getting paid on time for recurring work',
          'Scaling beyond hourly billing'
        ],
        solutions: [
          'Productized content packages',
          'Automated recurring billing',
          'Client self-service for add-ons',
          'Predictable monthly revenue'
        ],
        testimonial: {
          quote: 'We 3x our revenue by packaging our services. Edrano made it possible.',
          author: 'Rachel W.',
          role: 'Founder, WordCraft Content'
        }
      }
    ]
  },
  {
    id: 'professional',
    title: 'Professional Services',
    description: 'For consultants, coaches, and professional service providers',
    useCases: [
      {
        icon: Briefcase,
        title: 'Business Consultants',
        slug: 'consultants',
        description: 'Sell consulting packages, advisory retainers, and strategy sessions.',
        challenges: [
          'Pricing consulting intangibly',
          'Managing client expectations',
          'Scaling beyond time-for-money',
          'Looking professional to enterprise clients'
        ],
        solutions: [
          'Defined consulting packages with outcomes',
          'White-label portal with your brand',
          'Productized service offerings',
          'Professional client experience'
        ],
        testimonial: {
          quote: 'My Fortune 500 clients are impressed by the portal. It positions me as a premium consultant.',
          author: 'Tom H.',
          role: 'Strategy Consultant'
        }
      },
      {
        icon: GraduationCap,
        title: 'Coaches & Trainers',
        slug: 'coaches',
        description: 'Package coaching programs, memberships, and training courses.',
        challenges: [
          'Selling one-off sessions vs packages',
          'Managing coaching schedules',
          'Delivering course materials',
          'Building recurring revenue'
        ],
        solutions: [
          'Coaching packages with session bundles',
          'Calendar integration for booking',
          'Knowledge base for course delivery',
          'Subscription billing for memberships'
        ],
        testimonial: {
          quote: 'I finally have a real business, not just a coaching practice. MRR changed everything.',
          author: 'Jennifer L.',
          role: 'Executive Coach'
        }
      },
      {
        icon: Scale,
        title: 'Legal & Accounting Firms',
        slug: 'legal-accounting',
        description: 'Offer retainer packages, compliance services, and ongoing advisory.',
        challenges: [
          'Tracking billable hours',
          'Managing client documents',
          'Collecting retainer fees',
          'Client self-service for common questions'
        ],
        solutions: [
          'Retainer packages with clear scope',
          'Secure client portal',
          'Automated billing and payments',
          'FAQ knowledge base reduces calls'
        ],
        testimonial: {
          quote: 'Our clients can check their account status 24/7. Support calls dropped significantly.',
          author: 'Robert M.',
          role: 'Partner, M&M Legal'
        }
      },
      {
        icon: HeartHandshake,
        title: 'HR & Recruiting Agencies',
        slug: 'hr-recruiting',
        description: 'Sell recruiting packages, HR consulting, and ongoing talent services.',
        challenges: [
          'Pricing recruitment services',
          'Tracking placement guarantees',
          'Managing multiple hiring clients',
          'Building ongoing relationships'
        ],
        solutions: [
          'Recruitment packages with guarantees',
          'Client portal for job tracking',
          'Service catalog for HR add-ons',
          'Recurring billing for retained search'
        ],
        testimonial: {
          quote: 'We turned one-off placements into ongoing HR partnerships. Revenue is way more stable.',
          author: 'Amanda C.',
          role: 'Director, TalentFirst Agency'
        }
      }
    ]
  },
  {
    id: 'lifestyle',
    title: 'Lifestyle & Wellness',
    description: 'For health, fitness, and lifestyle service providers',
    useCases: [
      {
        icon: Dumbbell,
        title: 'Fitness & Personal Training',
        slug: 'fitness',
        description: 'Sell training packages, nutrition plans, and online coaching.',
        challenges: [
          'Managing client workout schedules',
          'Tracking subscription payments',
          'Delivering workout programs',
          'Upselling nutrition and supplements'
        ],
        solutions: [
          'Training packages with session counts',
          'Recurring billing for memberships',
          'Knowledge base for workout guides',
          'Easy upsell for additional services'
        ],
        testimonial: {
          quote: 'My online training business scaled to 100+ clients with Edrano automations.',
          author: 'Chris B.',
          role: 'Online Fitness Coach'
        }
      },
      {
        icon: Stethoscope,
        title: 'Health & Wellness Practitioners',
        slug: 'wellness',
        description: 'Package wellness programs, therapy sessions, and health coaching.',
        challenges: [
          'HIPAA-compliant client management',
          'Booking and payment collection',
          'Delivering care protocols',
          'Building patient relationships'
        ],
        solutions: [
          'Secure client portal',
          'Package-based care programs',
          'Calendar integration',
          'Knowledge base for patient education'
        ],
        testimonial: {
          quote: 'My patients love the self-service portal. They can book and pay without calling.',
          author: 'Dr. Susan P.',
          role: 'Naturopathic Doctor'
        }
      },
      {
        icon: Music,
        title: 'Music & Arts Instructors',
        slug: 'music-arts',
        description: 'Sell lesson packages, workshop series, and ongoing instruction.',
        challenges: [
          'Tracking lesson credits',
          'Managing student schedules',
          'Collecting payment consistently',
          'Offering group vs private lessons'
        ],
        solutions: [
          'Lesson packages with credit tracking',
          'Calendar booking integration',
          'Automated recurring billing',
          'Tiered pricing for different formats'
        ],
        testimonial: {
          quote: 'I finally get paid on time, every time. No more chasing students for fees.',
          author: 'Michael T.',
          role: 'Piano Instructor'
        }
      }
    ]
  },
  {
    id: 'agency',
    title: 'Agencies & Resellers',
    description: 'For agencies that resell or white-label services',
    useCases: [
      {
        icon: Building2,
        title: 'White-Label Resellers',
        slug: 'white-label',
        description: 'Resell hosting, marketing, or tech services under your own brand.',
        challenges: [
          'Managing multiple vendor relationships',
          'Branding services as your own',
          'Pricing for profit margins',
          'Client sees vendor names'
        ],
        solutions: [
          'Full white-label branding',
          'Custom domain support',
          'Your pricing, your margins',
          'Clients only see your brand'
        ],
        testimonial: {
          quote: 'We white-label hosting services. Our clients have no idea we use Edrano.',
          author: 'Alex K.',
          role: 'CEO, BrandedSites Agency'
        }
      },
      {
        icon: Users,
        title: 'Franchise & Multi-Location',
        slug: 'franchise',
        description: 'Manage multiple locations or franchise operations from one platform.',
        challenges: [
          'Consistency across locations',
          'Centralized reporting',
          'Local customization needs',
          'Team access management'
        ],
        solutions: [
          'Multi-tenant architecture',
          'Centralized analytics',
          'Per-location customization',
          'Role-based team permissions'
        ],
        testimonial: {
          quote: 'All 12 locations use the same system. Reporting is finally consistent.',
          author: 'Steve J.',
          role: 'Operations Director'
        }
      },
      {
        icon: ShoppingBag,
        title: 'Service Marketplaces',
        slug: 'marketplace',
        description: 'Run a marketplace connecting service providers with clients.',
        challenges: [
          'Managing multiple providers',
          'Splitting payments',
          'Quality control',
          'Scalable infrastructure'
        ],
        solutions: [
          'Multi-tenant platform',
          'Stripe Connect for splits',
          'Provider management',
          'Scalable architecture'
        ],
        testimonial: {
          quote: 'We launched our freelancer marketplace in weeks, not months.',
          author: 'Nina R.',
          role: 'Founder, SkillMatch'
        }
      }
    ]
  }
];

// ============================================
// COMPONENTS
// ============================================

function UseCaseCard({ useCase, categoryGradient }) {
  const Icon = useCase.icon;
  
  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-xl transition-all duration-300 group">
      {/* Header */}
      <div className={`p-6 bg-gradient-to-r ${categoryGradient}`}>
        <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
          <Icon className="w-7 h-7 text-stone-700" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">{useCase.title}</h3>
        <p className="text-white/80 text-sm">{useCase.description}</p>
      </div>
      
      {/* Content */}
      <div className="p-6">
        {/* Challenges */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-3">Common Challenges</h4>
          <ul className="space-y-2">
            {useCase.challenges.map((challenge, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-stone-600">
                <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                {challenge}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Solutions */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-3">How Edrano Helps</h4>
          <ul className="space-y-2">
            {useCase.solutions.map((solution, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-stone-600">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                {solution}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Testimonial */}
        {useCase.testimonial && (
          <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
            <p className="text-sm text-stone-700 italic mb-3">"{useCase.testimonial.quote}"</p>
            <div className="text-xs">
              <span className="font-semibold text-stone-900">{useCase.testimonial.author}</span>
              <span className="text-stone-500"> • {useCase.testimonial.role}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UseCaseCategory({ category, index }) {
  const gradients = [
    'from-violet-500 to-purple-600',
    'from-orange-500 to-pink-500',
    'from-cyan-500 to-blue-500',
    'from-emerald-500 to-teal-500',
    'from-amber-500 to-orange-500'
  ];
  
  const gradient = gradients[index % gradients.length];
  
  return (
    <section className="py-16" id={category.id}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-stone-900 mb-3">{category.title}</h2>
          <p className="text-lg text-stone-600">{category.description}</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {category.useCases.map((useCase) => (
            <UseCaseCard key={useCase.slug} useCase={useCase} categoryGradient={gradient} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-stone-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
              <span className="font-semibold">Edrano</span>
            </div>
            <p className="text-stone-400 text-sm">The client portal for service businesses.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <div className="space-y-2">
              <Link to="/features" className="block text-stone-400 hover:text-white text-sm">Features</Link>
              <Link to="/pricing" className="block text-stone-400 hover:text-white text-sm">Pricing</Link>
              <Link to="/use-cases" className="block text-stone-400 hover:text-white text-sm">Use Cases</Link>
              <Link to="/compare" className="block text-stone-400 hover:text-white text-sm">Compare</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <div className="space-y-2">
              <Link to="/docs" className="block text-stone-400 hover:text-white text-sm">Documentation</Link>
              <Link to="/docs/quick-start" className="block text-stone-400 hover:text-white text-sm">Getting Started</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <div className="space-y-2">
              <Link to="/privacy" className="block text-stone-400 hover:text-white text-sm">Privacy</Link>
              <Link to="/terms" className="block text-stone-400 hover:text-white text-sm">Terms</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-stone-800 pt-8 text-center text-stone-500 text-sm">
          © {new Date().getFullYear()} Edrano. All rights reserved.
          <span className="mx-2">•</span>
          Part of <a href="https://themelio.app" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">Themelio</a>
        </div>
      </div>
    </footer>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function UseCases() {
  const [activeCategory, setActiveCategory] = useState(null);

  // Flatten all use cases for quick navigation
  const allUseCases = USE_CASE_CATEGORIES.flatMap(cat => cat.useCases);

  return (
    <div className="min-h-screen bg-stone-50">
      <MorphingNavbar />
      
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-stone-100 to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            Built for service businesses
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
            Edrano works for<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-pink-500">
              every service business
            </span>
          </h1>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto mb-8">
            From WordPress agencies to fitness coaches, any business selling services and subscriptions can use Edrano to manage clients, automate billing, and grow revenue.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-stone-900 hover:bg-stone-800">
                Start free trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/app/demo">
              <Button size="lg" variant="outline">
                See live demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Category Quick Nav */}
      <section className="py-8 bg-white border-y border-stone-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {USE_CASE_CATEGORIES.map((category) => (
              <a
                key={category.id}
                href={`#${category.id}`}
                className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors bg-stone-100 text-stone-700 hover:bg-stone-200"
              >
                {category.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-stone-900 mb-2">20+</div>
              <div className="text-sm text-stone-500">Industries Served</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-stone-900 mb-2">500+</div>
              <div className="text-sm text-stone-500">Service Businesses</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-stone-900 mb-2">$2M+</div>
              <div className="text-sm text-stone-500">Processed Monthly</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-stone-900 mb-2">99.9%</div>
              <div className="text-sm text-stone-500">Uptime SLA</div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Case Categories */}
      {USE_CASE_CATEGORIES.map((category, index) => (
        <UseCaseCategory key={category.id} category={category} index={index} />
      ))}

      {/* Not Listed CTA */}
      <section className="py-20 bg-gradient-to-r from-violet-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Don't see your industry?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Edrano works for any business that sells services. If you manage clients and offer recurring services, we can help. Let's talk about your specific needs.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-violet-600 hover:bg-stone-100">
                Start free trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="mailto:hello@edrano.my">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Contact us
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
